<template>
	<div class="display-flex flex-direction-column">
		<form @submit.prevent>
			<h3 class="heading-5">What, if anything, has held you back from investing more into your account?</h3>
			<InputField :field="v$.questionOneResponse" field-name="Question One">
				<template #input>
					<ul class="radio-checkbox-list mt-100">
						<li v-for="option in questionOneOptions" :key="option.text" class="radio-checkbox-item">
							<label>
								<input
									v-model="v$.questionOneResponse.$model"
									type="radio"
									name="`question-one-${answer.text}`"
									:value="option.text"
								/>
								{{ option.text }} <span class="mask"></span>
							</label>
						</li>
					</ul>
				</template>
				<template #requiredIf-message> Please choose a response</template>
			</InputField>
			<TransitionCollapse>
				<div v-if="state.questionOneResponse === 'Other'">
					<InputField :field="v$.questionOneCustomResponse" field-name="Custom response">
						<template #input>
							<textarea
								v-model.trim="v$.questionOneCustomResponse.$model"
								class="input mt-100"
								maxlength="2000"
								name="question-one-other"
								cols="30"
								rows="2"
								placeholder="Is there anything else holding you back?"
							></textarea>
						</template>
						<template #requiredIf-message> Please tell us more</template>
					</InputField>
				</div>
			</TransitionCollapse>

			<h3 class="heading-5 mt-200"
				>What additional information about your portfolio would you find most valuable?</h3
			>
			<InputField :field="v$.questionTwoResponse" field-name="Question Two">
				<template #input>
					<ul class="radio-checkbox-list mt-100">
						<li v-for="option in questionTwoOptions" :key="option.text" class="radio-checkbox-item">
							<label>
								<input
									v-model="v$.questionTwoResponse.$model"
									type="radio"
									name="`question-two-${answer.text}`"
									:value="option.text"
								/>
								{{ option.text }} <span class="mask"></span>
							</label>
						</li>
					</ul>
				</template>
				<template #requiredIf-message> Please choose a response</template>
			</InputField>
			<TransitionCollapse>
				<div v-if="state.questionTwoResponse === 'Other'">
					<InputField :field="v$.questionTwoCustomResponse" field-name="Custom response 2">
						<template #input>
							<textarea
								v-model.trim="v$.questionTwoCustomResponse.$model"
								class="input mt-100"
								maxlength="2000"
								name="question-two-other"
								cols="30"
								rows="2"
								placeholder="Is there anything else you'd like us to know?"
							></textarea>
						</template>
						<template #requiredIf-message> Please tell us more</template>
					</InputField>
				</div>
			</TransitionCollapse>

			<h3 class="heading-5 mt-200"
				>Please share any other thoughts or feedback on your Fundrise investing experience.</h3
			>
			<textarea
				v-model.trim="additionalFeedback"
				class="input mt-100"
				maxlength="2000"
				name="additional-feedback"
				cols="30"
				rows="2"
				placeholder="Optional"
			></textarea>
			<div class="text-align-right mt-100">
				<SubmitButton :is-working="submitting" :v$="v$" @sb-click="submit()"> Submit </SubmitButton>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { required, requiredIf } from '@vuelidate/validators';
import { useRoute, useRouter } from 'vue-router';
import { apiRequest } from '@utils/request';
import { getDistinctId } from '@utils/analytics';
import InputField from '@components/form/input-field.vue';
import SubmitButton from '@components/form/submit-button.vue';
import TransitionCollapse from '@components/layout/transition-collapse.vue';
import { useVuelidate } from '@vuelidate/core';

interface HpiSurveyFormState {
	questionOneResponse: string;
	questionOneCustomResponse: string;
	questionTwoResponse: string;
	questionTwoCustomResponse: string;
}

const state: HpiSurveyFormState = reactive({
	questionOneResponse: '',
	questionOneCustomResponse: '',
	questionTwoResponse: '',
	questionTwoCustomResponse: ''
});

const rules = {
	questionOneResponse: {
		required
	},
	questionTwoResponse: {
		required
	},
	questionOneCustomResponse: {
		requiredIf: requiredIf(() => state.questionOneResponse === 'Other')
	},
	questionTwoCustomResponse: {
		requiredIf: requiredIf(() => state.questionTwoResponse === 'Other')
	}
};

const v$ = useVuelidate(rules, state);

const route = useRoute();
const router = useRouter();

const questionOneOptions: Array<{ text: string }> = [
	{
		text: 'I would like investment opportunities beyond real estate.'
	},
	{
		text: 'I would like to understand how my investment will grow over time.'
	},
	{
		text: 'I would like more direct control over where my investment goes.'
	},
	{
		text: 'I have unanswered questions about how Fundrise works.'
	},
	{
		text: "Nothing, I think my investment amount is fine where it's at."
	},
	{
		text: 'Other'
	}
];

const questionTwoOptions: Array<{ text: string }> = [
	{
		text: 'I would like more details about properties in my portfolio.'
	},
	{
		text: 'I would like continued investment and market education.'
	},
	{
		text: "I would like more details about our Fundrise's real estate strategies."
	},
	{
		text: 'I would like deeper metrics and analysis of asset returns, appreciation, and dividends.'
	},
	{
		text: 'Other'
	}
];

const submitting = ref(false);

const additionalFeedback = ref('');

const distinctId = computed((): string | undefined => (route.query.distinct_id as string) || getDistinctId());

async function submit(): Promise<void> {
	submitting.value = true;
	try {
		const surveyResponses = {
			mixpanelId: distinctId.value,
			questionOneResponse:
				state.questionOneResponse === 'Other' ? state.questionOneCustomResponse : state.questionOneResponse,
			questionTwoResponse:
				state.questionTwoResponse === 'Other' ? state.questionTwoCustomResponse : state.questionTwoResponse,
			additionalFeedback: additionalFeedback.value || undefined
		};

		await apiRequest.post(`survey/hpi/submit`, surveyResponses, {
			suppressToast: () => true
		});

		await router.push({ name: 'hpi-survey-success' });
	} catch {
	} finally {
		submitting.value = false;
	}
}
</script>
